import React, { useEffect, useRef, useState } from 'react'
import { CSmartTable } from '@coreui/react-pro'
import ClubService from 'src/service/ClubService'
import moment from 'moment'
import ToastComponent from 'src/components/common/TaostComponent'
import TableSearchToolbar from 'src/components/common/TableSearchToolbar'
import RowActionsMenu from 'src/components/common/RowActionsMenu'
import StatusBadge from 'src/components/common/StatusBadge'

const SEARCH_FIELDS = [
  { key: 'first_name', label: 'First Name' },
  { key: 'last_name', label: 'Last Name' },
  { key: 'email', label: 'Email' },
]

const SENT_STATUS = {
  0: { label: 'Not Sent', tone: 'secondary' },
  1: { label: 'Sent', tone: 'success' },
  2: { label: 'Joined', tone: 'info' },
}

const MemberInviteUserList = (props) => {
  const [loading, setLoading] = useState()
  const [activePage, setActivePage] = useState(1)
  const [columnFilter, setColumnFilter] = useState({})
  const [searchField, setSearchField] = useState('first_name')
  const [columnSorter, setColumnSorter] = useState(null)
  const [itemsPerPage, setItemsPerPage] = useState(20)
  const [users, setUsers] = useState([])

  const clearSearchKeys = (filter) => {
    const next = { ...filter }
    SEARCH_FIELDS.forEach((f) => delete next[f.key])
    return next
  }

  const applySearch = (value) => {
    setActivePage(1)
    const next = clearSearchKeys(columnFilter)
    if (value) next[searchField] = value
    setColumnFilter(next)
  }

  const handleFieldChange = (key) => {
    setSearchField(key)
    setColumnFilter((prev) => clearSearchKeys(prev))
  }

  const toggleAllCheckboxes = () => {
    const allIds = Array.isArray(users?.data) ? users.data.map((user) => user?.id) : []
    props?.setSelectedCheckboxes((prevSelected) =>
      prevSelected.length === allIds.length ? [] : allIds,
    )
  }

  const parentCheckboxRef = useRef(null)

  useEffect(() => {
    if (parentCheckboxRef.current) {
      parentCheckboxRef.current.indeterminate =
        props?.selectedCheckboxes.length > 0 &&
        props?.selectedCheckboxes.length < users?.data?.length
    }
  }, [props?.selectedCheckboxes, users?.data?.length])

  const columns = [
    {
      key: 'delete_fixture',
      label: (
        <input
          type="checkbox"
          name="delete_all"
          ref={parentCheckboxRef}
          checked={
            props?.selectedCheckboxes.length > 0 &&
            props?.selectedCheckboxes.length === users?.data?.length
          }
          onChange={toggleAllCheckboxes}
        />
      ),
      filter: false,
      sorter: false,
    },
    { label: 'First Name', key: 'first_name', filter: false },
    { label: 'Last Name', key: 'last_name', filter: false },
    { label: 'Email', key: 'email', filter: false },
    { label: 'Created On', filter: false, key: 'created_at' },
    { label: 'Sent', filter: false, sorter: true, key: 'is_sent' },
    {
      key: 'actions',
      label: 'Invite',
      filter: false,
      sorter: false,
    },
  ]

  useEffect(() => {
    if (!props?.gameId) {
      return
    }

    setLoading(true)
    const offset = itemsPerPage * (activePage - 1)
    const params = new URLSearchParams()

    if (columnFilter && Object.keys(columnFilter).length > 0) {
      Object.keys(columnFilter).forEach((key) => {
        params.append(key, columnFilter[key])
      })
    }

    if (columnSorter && columnSorter.column) {
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)
    }

    ClubService.getUserFromGame(offset, itemsPerPage, activePage, params, props?.gameId)
      .then((result) => {
        setUsers(result.data)
      })
      .catch((error) => {
        console.error('Error fetching users:', error)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [activePage, columnFilter, columnSorter, itemsPerPage, props?.gameId])

  const toggleCheckbox = (id) => {
    props?.setSelectedCheckboxes((prevSelected) => {
      if (prevSelected.includes(id)) {
        return prevSelected.filter((selectedId) => selectedId !== id)
      } else {
        return [...prevSelected, id]
      }
    })
  }

  const sendCode = (id) => {
    const payload = { userIds: [id], gameId: props.gameId }
    ClubService.sendMemberInvite(payload).then((res) => {
      if (res.status === 200) {
        ToastComponent(res.message, 'success')
        props.fetchData(props.gameId)
      }
    })
  }

  const activeSearchLabel = SEARCH_FIELDS.find((f) => f.key === searchField)?.label

  return (
    <>
      <TableSearchToolbar
        fields={SEARCH_FIELDS}
        field={searchField}
        onFieldChange={handleFieldChange}
        value={columnFilter?.[searchField] || ''}
        onSearch={applySearch}
        placeholder={`Search by ${activeSearchLabel}...`}
        trailing={props.toolbarTrailing}
      />
      <div className="ge-datatable">
        <CSmartTable
          columns={columns}
          columnSorter={{ external: true }}
          scopedColumns={{
            delete_fixture: (item) => (
              <th>
                <input
                  type="checkbox"
                  name="delete"
                  value={item.id}
                  checked={props?.selectedCheckboxes.includes(item.id)}
                  onChange={() => toggleCheckbox(item.id)}
                />
              </th>
            ),
            created_at: (item) => <td> {moment(item.updated_at).format('D.MM.YYYY')}</td>,
            is_sent: (item) => {
              const status = SENT_STATUS[item.is_sent] || SENT_STATUS[0]
              return (
                <td>
                  <StatusBadge label={status.label} tone={status.tone} />
                </td>
              )
            },
            actions: (item) => (
              <td>
                <RowActionsMenu
                  actions={[
                    item.is_sent === 0 && { label: 'Send', onClick: () => sendCode(item.id) },
                    item.is_sent === 1 && { label: 'Re-send', onClick: () => sendCode(item.id) },
                    item.is_sent === 2 && { label: 'Joined', disabled: true },
                  ].filter(Boolean)}
                />
              </td>
            ),
          }}
          items={users?.data}
          itemsPerPage={itemsPerPage}
          itemsPerPageSelect
          loading={loading}
          pagination={{ external: true }}
          paginationProps={{
            activePage: activePage,
            pages: Math.ceil(users?.total / itemsPerPage) || 1,
          }}
          tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
          onActivePageChange={(activePage) => setActivePage(activePage)}
          onItemsPerPageChange={(itemsPerPage) => {
            setActivePage(1)
            setItemsPerPage(itemsPerPage)
          }}
          onSorterChange={(sorter) => setColumnSorter(sorter)}
        />
      </div>
    </>
  )
}

export default MemberInviteUserList
